import { ActionButton } from '@/components/common/ActionButton';
import { ChoiceCellEditor } from '@/components/common/ChoiceCellEditor';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { MOTION } from '@/components/common/motion';
import { PageHeader } from '@/components/common/PageHeader';
import { Reveal } from '@/components/common/Reveal';
import { TableSkeleton } from '@/components/common/Skeleton';
import { AssemblyDrawingDialog } from '@/components/dialogs/AssemblyDrawingDialog';
import { TOOL_MANAGEMENT_DIALOG_PT } from '@/components/dialogs/dialogTheme';
import { PredictToolsDialog } from '@/components/dialogs/PredictToolsDialog';
import { AssemblyThumbnail } from '@/components/tools/AssemblyThumbnail';
import { ColumnChooser } from '@/components/tools/ColumnChooser';
import { ToolImportDropZone } from '@/components/tools/ToolImportDropZone';
import { ToolImportReportDialog } from '@/components/tools/ToolImportReportDialog';
import type { ColumnAlign, ColumnDef, ColumnKey } from '@/components/tools/toolColumns';
import {
  ACTIONS_COL_WIDTH,
  clearStoredColumnLayout,
  COLUMNS,
  DEFAULT_COL_ORDER,
  DEFAULT_COL_WIDTHS,
  DEFAULT_VISIBLE_KEYS,
  isDerivedColumn,
  isReadOnlyColumn,
  loadColOrder,
  loadColVisibility,
  loadColWidths,
  LS_COL_ORDER_KEY,
  LS_COL_WIDTHS_KEY,
  MIN_COL_WIDTH,
  PHONE_ACTIONS_COL_WIDTH,
  PHONE_PINNED_COL_WIDTH,
  PINNED_COLUMN,
  saveColVisibility,
} from '@/components/tools/toolColumns';
import { clearAssemblyDrawingCache } from '@/hooks/useAssemblyThumbnail';
import { useHolders } from '@/hooks/useHolders';
import { MEDIA_BELOW_MD, useMediaQuery } from '@/hooks/useMediaQuery';
import { useToolAssemblies } from '@/hooks/useToolAssemblies';
import { useTools } from '@/hooks/useTools';
import type { AssemblyModel } from '@/types/assembly.types';
import type {
  CreateHolder,
  CreateTool,
  HolderModel,
  ToolModel,
  ToolType,
  UpdateHolder,
  UpdateTool,
} from '@/types/tool.types';
import { TOOL_PURPOSE_VALUES, TOOL_TYPE_VALUES } from '@/types/tool.types';
import type { ToolImportReport } from '@/types/toolImport.types';
import { formatDateTime, formatMm } from '@/utils/formatters';
import {
  STICKY_LEFT_CELL,
  STICKY_LEFT_CELL_ELEVATED,
  STICKY_LEFT_HEADER,
  STICKY_LEFT_HEADER_ELEVATED,
  STICKY_RIGHT_CELL,
  STICKY_RIGHT_CELL_ELEVATED,
  STICKY_RIGHT_HEADER,
  STICKY_RIGHT_HEADER_ELEVATED,
  TABLE_CARD,
  TABLE_FOOTER,
  TABLE_HEADER_CELL,
  TABLE_HEADER_ROW,
  TABLE_ROW,
} from '@/utils/stickyColumns';
import {
  CaretLeft,
  CaretRight,
  CircleNotch,
  Columns,
  DotsSixVertical,
  FunnelSimple,
  Info,
  MagicWand,
  Plus,
  Question,
  Toolbox,
  Trash,
  UploadSimple,
  Wrench,
  X,
} from '@phosphor-icons/react';
import type {
  DragEvent as ReactDragEvent,
  FC,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';

/** Pending edits per tool id. Only changed fields are sent on submit. */
type EditsMap = Record<string, Partial<UpdateTool>>;

/** Exact-value filters: click on a value to filter, click again to clear. */
type FilterValues = Partial<Record<keyof UpdateTool, string | number | boolean>>;

type EditCell = { toolId: string; field: keyof UpdateTool } | null;

function getCellValue<T>(tool: ToolModel, toolId: string, field: keyof UpdateTool, edits: EditsMap): T {
  const e = edits[toolId];
  if (e && field in e && (e[field] !== undefined || e[field] === null)) {
    return e[field] as T;
  }
  return (tool as unknown as Record<string, unknown>)[field] as T;
}

function valuesEqual(a: string | number | boolean, b: string | number | boolean): boolean {
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b;
  return String(a) === String(b);
}

function blockInvalidNumberKeys(e: ReactKeyboardEvent<HTMLInputElement>): void {
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return;
  }

  const allowedNavigationKeys = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]);

  if (allowedNavigationKeys.has(e.key)) {
    return;
  }

  if (e.key.length !== 1) {
    return;
  }

  const input = e.currentTarget;
  const value = input.value;
  const hasSelection = input.selectionStart !== input.selectionEnd;

  // Accept digits unconditionally.
  if (/[0-9]/.test(e.key)) {
    return;
  }

  // Accept one decimal separator.
  if (e.key === '.' || e.key === ',') {
    if (value.includes('.') || value.includes(',')) {
      e.preventDefault();
    }
    return;
  }

  // Accept minus sign only at the start and only once.
  if (e.key === '-') {
    const cursorAtStart = input.selectionStart === 0;
    if (!cursorAtStart || (value.includes('-') && !hasSelection)) {
      e.preventDefault();
    }
    return;
  }

  e.preventDefault();
}

type BallTypeDropdownValue = 'unset' | 'true' | 'false';

function ballTypeToDropdown(v: boolean | null | undefined): BallTypeDropdownValue {
  if (v === true) return 'true';
  if (v === false) return 'false';
  return 'unset';
}

/** Placeholder for a cell with nothing to show, matching the rest of the page. */
const NO_VALUE = '—';

/**
 * The pill a yes/no cell reads as.
 *
 * Tighter than the shared `CHIP` because it sits inside a cell button that already carries its
 * own padding, and written out rather than composed from it for the same reason the preset chips
 * are: the retired state swaps both background and text colour, and two utilities for one
 * property on one element are settled by stylesheet order.
 */
const STATUS_CHIP = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';

/**
 * A column's alignment as the one class that sets it.
 *
 * The header, the value button and the open editor all read this map, which is what keeps a
 * measurement from jumping sideways the moment it is double-clicked. A map rather than a
 * ternary chain because two alignment utilities on one element would be settled by stylesheet
 * order, and because a fourth alignment would otherwise mean finding every branch again.
 */
const ALIGN_TEXT_CLASS: Record<ColumnAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

/** The same alignment for the header's flex row of grip handle, label and unit. */
const ALIGN_JUSTIFY_CLASS: Record<ColumnAlign, string> = {
  left: 'justify-start',
  right: 'justify-end',
  center: 'justify-center',
};

/** Shown while the assemblies are still on their way, so an empty cell is not read as "none". */
const PENDING_VALUE = '…';

/** Distinct holder names of a tool's assemblies, keeping the NC-number order of the list. */
function holderNamesOf(assemblies: AssemblyModel[]): string[] {
  const names: string[] = [];
  for (const assembly of assemblies) {
    const name = assembly.holder?.holder_name;
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

/** NC numbers of a tool's assemblies; the list arrives already sorted ascending. */
function ncNumbersOf(assemblies: AssemblyModel[]): number[] {
  return assemblies.map((a) => a.nc_number).filter((n): n is number => n != null);
}

const DEFAULT_ADD_TOOL_FORM: CreateTool = {
  // Basic
  tool_name: '',
  tool_type: 'MfgDrillTool',
  tool_comment: '',
  // Core dimensions
  tool_diameter: 0,
  tool_overall_len: 0,
  tool_len: 0,
  // Optional dimensions
  tool_corner_rad: null,
  tool_cut_angle: null,
  tool_cut_len: null,
  // Rotation
  tool_rotation: 'CLW',
  // Angle geometry
  horizontal_distance: null,
  vertical_distance: null,
  tip_angle: null,
  flank_angle: null,
  // Left out so the backend applies its own default (false)
  ball_type: undefined,
};

/**
 * The four numeric holder columns. They differ only by which field they read, so the cell is
 * written once and the list decides how many of them there are.
 */
const HOLDER_DIMENSION_FIELDS = ['diameter_mm_1', 'diameter_mm_2', 'len_mm_1', 'len_mm_2'] as const;

/**
 * Paint containment on a card that holds a horizontally scrolling table.
 *
 * A table wider than its `overflow-x-auto` parent is clipped visually, but Chrome still adds its
 * width to the scrollable overflow of every ancestor, all the way up to the document — so on a
 * 400 px screen the whole page gained a horizontal scrollbar and the header slid out of view.
 * Neither `overflow-hidden` on the card nor `overflow-x: hidden` on `html`/`body` stops that;
 * paint containment does, because it makes the card an explicit clipping boundary.
 *
 * It is safe for the pinned columns: `position: sticky` resolves against the inner scroll
 * container, which is untouched.
 */
const CARD_CONTAINMENT = '[contain:paint]';

/** Sum of the holders colgroup: 240 name + 4x112 dimensions + 64 actions. */
const HOLDERS_TABLE_MIN_WIDTH = 240 + HOLDER_DIMENSION_FIELDS.length * 112 + 64;

export const ToolManagement: FC = () => {
  const { t } = useTranslation();
  const {
    tools,
    isLoading,
    error,
    refetch,
    createTool,
    updateTool,
    deleteTool,
    page,
    setPage,
    pageSize,
    hasNextPage,
    hasPrevPage,
    totalPages,
    totalCount,
    listFilter,
    setListFilter,
  } = useTools();
  const {
    holders,
    isLoading: holdersLoading,
    addHolder,
    updateHolder,
    deleteHolder,
    refetch: refetchHolders,
  } = useHolders();
  const {
    byToolId: assembliesByToolId,
    isLoading: assembliesLoading,
    error: assembliesError,
    refetch: refetchAssemblies,
  } = useToolAssemblies();
  const [edits, setEdits] = useState<EditsMap>({});
  /** Latest `edits` for submit — avoids stale `useCallback` closure and matches state after child updates. */
  const editsRef = useRef<EditsMap>({});
  editsRef.current = edits;
  const [submitting, setSubmitting] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [editingCell, setEditingCell] = useState<EditCell>(null);
  const [showAddHolderDialog, setShowAddHolderDialog] = useState(false);
  const [showDimensionsModal, setShowDimensionsModal] = useState(false);
  const [addHolderForm, setAddHolderForm] = useState<CreateHolder>({
    holder_name: '',
    diameter_mm_1: 0,
    diameter_mm_2: 0,
    len_mm_1: 0,
    len_mm_2: 0,
  });
  const [addingHolder, setAddingHolder] = useState(false);
  const [showAddToolDialog, setShowAddToolDialog] = useState(false);
  /** hyperMILL import lives in a dialog: it is a rare action and does not earn room on the page. */
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPredictDialog, setShowPredictDialog] = useState(false);
  /** Report of the last hyperMILL import; the report dialog renders from it. */
  const [importReport, setImportReport] = useState<ToolImportReport | null>(null);
  const [showImportReport, setShowImportReport] = useState(false);
  const [addToolForm, setAddToolForm] = useState<CreateTool>(DEFAULT_ADD_TOOL_FORM);
  const [addingTool, setAddingTool] = useState(false);
  /** Tool whose assembly drawing is open; null closes the drawing dialog. */
  const [drawingTool, setDrawingTool] = useState<ToolModel | null>(null);
  type HolderEditCell = { holderId: string; field: keyof UpdateHolder };
  const [editingHolderCell, setEditingHolderCell] = useState<HolderEditCell | null>(null);
  const [holderEditValue, setHolderEditValue] = useState<string | number | null>(null);
  /** Tool id for which tool_type update is in progress. */
  const [toolTypeUpdatingToolId, setToolTypeUpdatingToolId] = useState<string | null>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  /** Anchor for the row-interaction help overlay opened by the "?" button above the table. */
  const rowInteractionHelpRef = useRef<OverlayPanel>(null);
  const [colWidths, setColWidths] = useState<Record<string, number>>(loadColWidths);
  const colWidthsRef = useRef(colWidths);
  colWidthsRef.current = colWidths;
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);
  const [colOrder, setColOrder] = useState<ColumnKey[]>(loadColOrder);
  /** Which columns the table shows, in no particular order; the order lives in `colOrder`. */
  const [visibleColKeys, setVisibleColKeys] = useState<ColumnKey[]>(loadColVisibility);
  /** Anchor for the column chooser opened by the "Sloupce" button above the table. */
  const columnChooserRef = useRef<OverlayPanel>(null);
  /**
   * Two pinned columns have to fit beside a readable middle on a 400 px screen, and a
   * `colgroup` width is a number in JavaScript rather than a class — so the breakpoint has to
   * be answered here rather than with `md:`.
   */
  const isBelowMd = useMediaQuery(MEDIA_BELOW_MD);
  const [dragOverKey, setDragOverKey] = useState<ColumnKey | null>(null);
  const [dragSourceKey, setDragSourceKey] = useState<ColumnKey | null>(null);
  const dragStartKeyRef = useRef<ColumnKey | null>(null);
  const [filterDraftToolTypes, setFilterDraftToolTypes] = useState<string[]>([]);
  const [filterDraftOrder, setFilterDraftOrder] = useState<'asc' | 'desc'>('asc');
  /** The horizontally scrolling middle of the table; the pinned columns stand still against it. */
  const tableScrollRef = useRef<HTMLDivElement>(null);
  /**
   * Whether there is content hidden past each edge.
   *
   * Drives the drop shadow on the pinned columns: a seam is always drawn, but the shadow
   * only appears on the side that actually has rows sliding underneath it.
   */
  const [scrolledEdges, setScrolledEdges] = useState({ start: false, end: false });

  const listFilterRef = useRef(listFilter);
  listFilterRef.current = listFilter;
  const toolsRef = useRef(tools);
  toolsRef.current = tools;

  const visibleColSet = useMemo(() => new Set<string>(visibleColKeys), [visibleColKeys]);

  /**
   * The columns actually rendered: saved order, filtered by the saved visible set.
   *
   * `colOrder` deliberately keeps every key, hidden ones included, so switching a column back on
   * returns it to where the user last dragged it rather than to the end of the table.
   */
  const orderedColumns = useMemo<ColumnDef[]>(
    () =>
      colOrder
        .filter((key) => visibleColSet.has(key))
        .map((key) => COLUMNS.find((c) => c.key === key))
        .filter((col): col is ColumnDef => col != null),
    [colOrder, visibleColSet]
  );

  /**
   * A column's rendered width.
   *
   * Below `md` the pinned column ignores both the saved width and its default: 268 px of a
   * 400 px viewport leaves no room for the middle of the table, which is the whole reason the
   * pinning used to be switched off down there. Stored widths apply from `md` up.
   */
  const columnWidth = useCallback(
    (key: ColumnKey): number => {
      if (isBelowMd && key === PINNED_COLUMN) return PHONE_PINNED_COL_WIDTH;
      return colWidths[key] ?? DEFAULT_COL_WIDTHS[key] ?? 0;
    },
    [colWidths, isBelowMd]
  );

  const actionsColWidth = isBelowMd ? PHONE_ACTIONS_COL_WIDTH : ACTIONS_COL_WIDTH;

  /** Options are fixed lists, so they are built once rather than per rendered cell. */
  const toolTypeOptions = useMemo(() => TOOL_TYPE_VALUES.map((tt) => ({ label: tt, value: tt })), []);
  const purposeOptions = useMemo(
    () => TOOL_PURPOSE_VALUES.map((tp) => ({ label: t(`toolManagement.purpose_${tp}`), value: tp })),
    [t]
  );
  /** The editor speaks strings, the field is a boolean; these two are the whole mapping. */
  const booleanOptions = useMemo(
    () => [
      { label: t('common.yes'), value: 'true' },
      { label: t('common.no'), value: 'false' },
    ],
    [t]
  );

  /**
   * Width the table insists on, summed from the column map.
   *
   * `table-fixed` with `w-full` would otherwise squeeze the columns into the container and
   * never overflow, so there would be nothing for the pinned columns to stay still against.
   */
  const totalTableWidth =
    orderedColumns.reduce((sum, col) => sum + columnWidth(col.key), 0) + actionsColWidth;

  /** Recompute which edges have content behind them; cheap enough to run on every scroll. */
  const syncScrolledEdges = useCallback(() => {
    const element = tableScrollRef.current;
    if (!element) return;
    const start = element.scrollLeft > 1;
    const end = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
    setScrolledEdges((previous) => (previous.start === start && previous.end === end ? previous : { start, end }));
  }, []);

  /**
   * Measure as soon as the scroll container exists.
   *
   * An effect alone would miss the first measurement: the table only mounts once the
   * tools have arrived, which is a render the effect's dependencies do not notice.
   */
  const attachTableScroll = useCallback(
    (node: HTMLDivElement | null) => {
      tableScrollRef.current = node;
      if (node) syncScrolledEdges();
    },
    [syncScrolledEdges]
  );

  // The answer changes without a scroll event too: the window resizes, or a column is
  // dragged wider or narrower.
  // biome-ignore lint/correctness/useExhaustiveDependencies: totalTableWidth is not read here — it is what re-runs the measurement after a column resize.
  useEffect(() => {
    syncScrolledEdges();
    window.addEventListener('resize', syncScrolledEdges);
    return () => {
      window.removeEventListener('resize', syncScrolledEdges);
    };
  }, [syncScrolledEdges, totalTableWidth]);

  /**
   * A cell cannot stay in edit mode once its column is switched off.
   *
   * The editor unmounts with the column, but `editingCell` would keep pointing at it — which is
   * what the header reads to decide whether to offer "Save changes", so the page would be left
   * offering to save an edit the user can no longer see. Pending *edits* are deliberately kept:
   * hiding a column is a view choice, not an undo.
   */
  useEffect(() => {
    if (editingCell != null && !visibleColSet.has(editingCell.field)) setEditingCell(null);
  }, [editingCell, visibleColSet]);

  useEffect(() => {
    if (!showFilterModal) return;
    const filter = listFilterRef.current;
    setFilterDraftToolTypes(
      filter.tool_types == null ? [...TOOL_TYPE_VALUES] : filter.tool_types.length === 0 ? [] : filter.tool_types
    );
    setFilterDraftOrder(filter.order ?? 'asc');
  }, [showFilterModal]);

  const filteredTools = useMemo(() => {
    const keys = Object.keys(filterValues) as (keyof UpdateTool)[];
    if (keys.length === 0) return tools;
    return tools.filter((tool) => {
      return keys.every((field) => {
        const filterVal = filterValues[field];
        if (filterVal === undefined) return true;
        // `?? ''` so the nullable measurement columns filter on "has no value" rather than
        // comparing the string "null" against the empty string and matching nothing.
        const cellVal = getCellValue<unknown>(tool, tool.id, field, edits) ?? '';
        return valuesEqual(cellVal as string | number | boolean, filterVal);
      });
    });
  }, [tools, filterValues, edits]);

  const toggleFilter = useCallback((field: keyof UpdateTool, value: string | number | boolean) => {
    setFilterValues((prev) => {
      const current = prev[field];
      if (current !== undefined && valuesEqual(current, value)) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const clearFilter = useCallback((field: keyof UpdateTool) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const openAssemblyDrawing = useCallback((tool: ToolModel) => {
    setDrawingTool(tool);
  }, []);

  const closeAssemblyDrawing = useCallback(() => {
    setDrawingTool(null);
  }, []);

  /**
   * An import rewrote the catalogue: say what landed, show the full report, reload the page data.
   *
   * Holders and assemblies are reloaded alongside the tools because the import fills all
   * three, and the assemblies feed the table's holder and NC-number cells. The cached
   * thumbnails go too — the import can have moved the geometry they were drawn from.
   */
  const handleToolsImported = useCallback(
    async (report: ToolImportReport) => {
      setShowImportDialog(false);
      toast.success(t('toolManagement.importSuccess', { tools: report.tools, assemblies: report.assemblies }));
      setImportReport(report);
      setShowImportReport(true);
      clearAssemblyDrawingCache();
      await Promise.all([refetch(), refetchHolders(), refetchAssemblies()]);
    },
    [refetch, refetchHolders, refetchAssemblies, t]
  );

  /**
   * A plain click on a value cell does nothing; Ctrl/⌘-click filters by that exact value.
   *
   * The drawing used to open on a plain click, which meant every click had to wait out a
   * double-click window before anything happened. The thumbnail in the name column is the
   * affordance for that now, so the delay is gone and a double-click edits immediately.
   */
  const handleCellClick = useCallback(
    (field: keyof UpdateTool, value: string | number | boolean, filterModifier: boolean) => {
      if (!filterModifier) return;
      toggleFilter(field, value);
    },
    [toggleFilter]
  );

  const handleCellDoubleClick = useCallback((toolId: string, field: keyof UpdateTool) => {
    setEditingCell({ toolId, field });
  }, []);

  const setEdit = useCallback((toolId: string, field: keyof UpdateTool, value: string | number | boolean | null) => {
    setEdits((prev) => {
      const next = { ...prev };
      const toolEdits = { ...next[toolId] };
      const originalTool = toolsRef.current.find((t) => t.id === toolId);
      const originalValue = originalTool ? (originalTool as unknown as Record<string, unknown>)[field] : undefined;
      const isUnchanged = value !== null && value !== undefined && value === originalValue;
      if (value === null || value === undefined || isUnchanged) {
        if (field in toolEdits) {
          delete toolEdits[field];
        }
      } else {
        (toolEdits as Record<string, unknown>)[field] = value;
      }
      if (Object.keys(toolEdits).length === 0) {
        delete next[toolId];
      } else {
        next[toolId] = toolEdits;
      }
      editsRef.current = next;
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    const snapshot = editsRef.current;
    const ids = Object.keys(snapshot);
    if (ids.length === 0) return;

    setSubmitting(true);
    const successIds = new Set<string>();
    let failCount = 0;

    for (const id of ids) {
      const body = snapshot[id];
      if (!body || Object.keys(body).length === 0) continue;

      try {
        await updateTool(id, body);
        successIds.add(id);
      } catch {
        failCount += 1;
      }
    }

    setSubmitting(false);
    // Diameters and lengths are what the assembly drawing is built from, so a saved edit
    // invalidates the thumbnails even though no assembly id changed.
    if (successIds.size > 0) clearAssemblyDrawingCache();
    if (failCount === 0) {
      setEdits({});
      setEditingCell(null);
      await Promise.all([refetch(), refetchAssemblies()]);
      toast.success(t('toolManagement.saveSuccess'));
    } else {
      toast.error(t('toolManagement.saveError'));
      setEdits((prev) => {
        const next: EditsMap = {};
        for (const id of Object.keys(prev)) {
          if (!successIds.has(id) && prev[id]) next[id] = prev[id];
        }
        return next;
      });
      if (successIds.size > 0) await Promise.all([refetch(), refetchAssemblies()]);
    }
  }, [updateTool, refetch, refetchAssemblies, t]);

  const dirtyCount = Object.keys(edits).length;
  const activeFilterEntries = Object.entries(filterValues) as [keyof UpdateTool, string | number | boolean][];

  const handleAddHolder = useCallback(async () => {
    setAddingHolder(true);
    try {
      await addHolder(addHolderForm);
      setShowAddHolderDialog(false);
      setAddHolderForm({
        holder_name: '',
        diameter_mm_1: 0,
        diameter_mm_2: 0,
        len_mm_1: 0,
        len_mm_2: 0,
      });
      await refetchHolders();
    } catch {
      // toast already in addHolder
    } finally {
      setAddingHolder(false);
    }
  }, [addHolder, addHolderForm, refetchHolders]);

  const handleAddTool = useCallback(async () => {
    if (!addToolForm.tool_name.trim()) {
      toast.error(t('toolManagement.addError'));
      return;
    }
    setAddingTool(true);
    try {
      await createTool(addToolForm);
      setShowAddToolDialog(false);
      setAddToolForm(DEFAULT_ADD_TOOL_FORM);
      await refetchAssemblies();
      toast.success(t('toolManagement.addSuccess'));
    } catch {
      toast.error(t('toolManagement.addError'));
    } finally {
      setAddingTool(false);
    }
  }, [addToolForm, createTool, refetchAssemblies, t]);

  const handleDeleteHolder = useCallback(
    async (holderId: string) => {
      if (!window.confirm(t('holders.deleteConfirm'))) return;
      try {
        await deleteHolder(holderId);
      } catch {
        // toast in deleteHolder
      }
    },
    [deleteHolder, t]
  );

  const handleDeleteTool = useCallback(
    async (toolId: string) => {
      if (!window.confirm(t('toolManagement.deleteConfirm'))) return;
      try {
        await deleteTool(toolId);
        await refetchAssemblies();
        toast.success(t('toolManagement.deleteSuccess'));
      } catch {
        toast.error(t('toolManagement.deleteError'));
      }
    },
    [deleteTool, refetchAssemblies, t]
  );

  const startEditingHolderCell = useCallback((holder: HolderModel, field: keyof UpdateHolder) => {
    const raw = (holder as unknown as Record<string, unknown>)[field];
    setHolderEditValue(raw !== undefined && raw !== null ? (typeof raw === 'number' ? raw : String(raw)) : '');
    setEditingHolderCell({ holderId: holder.id, field });
  }, []);

  const handleHolderCellSave = useCallback(
    async (holderId: string, field: keyof UpdateHolder, value: string | number) => {
      setEditingHolderCell(null);
      setHolderEditValue(null);
      const holder = holders.find((h) => h.id === holderId);
      if (!holder) return;
      const current = (holder as unknown as Record<string, unknown>)[field];
      if (current === value) return;
      try {
        await updateHolder(holderId, { [field]: value });
      } catch {
        // toast handled by updateHolder or API
      }
    },
    [holders, updateHolder]
  );

  /** On tool_type select in tools table: send update immediately and close dropdown. */
  const handleToolTypeSelect = useCallback(
    async (toolId: string, toolType: ToolType) => {
      setToolTypeUpdatingToolId(toolId);
      try {
        await updateTool(toolId, { tool_type: toolType });
        setEdits((prev) => {
          const next = { ...prev };
          const toolEdits = { ...next[toolId] };
          delete toolEdits.tool_type;
          if (Object.keys(toolEdits).length === 0) delete next[toolId];
          else next[toolId] = toolEdits;
          return next;
        });
        toast.success(t('toolManagement.saveSuccess'));
      } catch {
        toast.error(t('toolManagement.saveError'));
      } finally {
        setToolTypeUpdatingToolId(null);
      }
    },
    [updateTool, t]
  );

  const filterLabel = useCallback(
    (field: keyof UpdateTool, value: string | number | boolean) => {
      if (typeof value === 'boolean') {
        // The chip has to read like the cell it came from, and the status column says
        // "Aktivní" / "Vyřazený" where every other yes/no column says "Ano" / "Ne".
        if (field === 'active') return t(value ? 'toolManagement.activeYes' : 'toolManagement.activeNo');
        return t(value ? 'common.yes' : 'common.no');
      }
      // Filtering on a column that is empty for this row is a real query ("which tools have no
      // comment"), so the chip needs a mark rather than the empty string it would render.
      return value === '' ? NO_VALUE : String(value);
    },
    [t]
  );

  const handleResizeMouseDown = useCallback((col: ColumnKey, e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidthsRef.current[col] ?? DEFAULT_COL_WIDTHS[col] ?? 80;
    resizingRef.current = { col, startX, startWidth };

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const delta = ev.clientX - resizingRef.current.startX;
      const newWidth = Math.max(MIN_COL_WIDTH, resizingRef.current.startWidth + delta);
      setColWidths((prev) => ({ ...prev, [resizingRef.current!.col]: newWidth }));
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setColWidths((prev) => {
        try {
          localStorage.setItem(LS_COL_WIDTHS_KEY, JSON.stringify(prev));
        } catch {
          // ignore
        }
        return prev;
      });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleColDragStart = useCallback((key: ColumnKey, e: ReactDragEvent) => {
    dragStartKeyRef.current = key;
    setDragSourceKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key); // required for Firefox
  }, []);

  const handleColDragOver = useCallback((key: ColumnKey, e: ReactDragEvent) => {
    if (!dragStartKeyRef.current || dragStartKeyRef.current === key) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverKey(key);
  }, []);

  const handleColDragLeave = useCallback((e: ReactDragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      setDragOverKey(null);
    }
  }, []);

  const handleColDrop = useCallback((targetKey: ColumnKey, e: ReactDragEvent) => {
    e.preventDefault();
    const sourceKey = dragStartKeyRef.current;
    if (!sourceKey || sourceKey === targetKey) {
      setDragOverKey(null);
      return;
    }
    setColOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(sourceKey);
      const toIdx = next.indexOf(targetKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, sourceKey);
      try {
        localStorage.setItem(LS_COL_ORDER_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
    setDragOverKey(null);
    setDragSourceKey(null);
    dragStartKeyRef.current = null;
  }, []);

  const handleColDragEnd = useCallback(() => {
    setDragOverKey(null);
    setDragSourceKey(null);
    dragStartKeyRef.current = null;
  }, []);

  /**
   * One column switched on or off.
   *
   * The pinned column cannot be switched off — the chooser renders its row disabled — but the
   * guard stays, because a stored visible set is user-editable data and a table whose first
   * column is missing has nowhere to put the thumbnail or the delete affordance.
   */
  const handleToggleColumn = useCallback(
    (key: ColumnKey, visible: boolean) => {
      if (key === PINNED_COLUMN) return;
      const next = visible
        ? visibleColKeys.includes(key)
          ? visibleColKeys
          : [...visibleColKeys, key]
        : visibleColKeys.filter((k) => k !== key);
      saveColVisibility(next);
      setVisibleColKeys(next);
    },
    [visibleColKeys]
  );

  const handleApplyColumnPreset = useCallback((keys: ColumnKey[]) => {
    const next = keys.includes(PINNED_COLUMN) ? [...keys] : [PINNED_COLUMN, ...keys];
    saveColVisibility(next);
    setVisibleColKeys(next);
  }, []);

  /** Visible set, order and widths all back to the defaults, and the saved layout forgotten. */
  const handleResetColumns = useCallback(() => {
    clearStoredColumnLayout();
    setVisibleColKeys([...DEFAULT_VISIBLE_KEYS]);
    setColOrder([...DEFAULT_COL_ORDER]);
    setColWidths({ ...DEFAULT_COL_WIDTHS });
  }, []);

  /** Live counts under the h1; a part that has not loaded yet is left out rather than shown as 0. */
  const subtitle = [
    totalCount != null ? t('toolManagement.subtitleTools', { count: totalCount }) : null,
    holdersLoading ? null : t('holders.subtitleHolders', { count: holders.length }),
  ]
    .filter(Boolean)
    .join(' · ');

  /** Summary of the list filter that lives in the URL, as opposed to the exact-value chips. */
  const listFilterSummary =
    listFilter.tool_types != null || listFilter.order !== 'asc'
      ? [
          listFilter.tool_types != null && listFilter.tool_types.length > 0
            ? `${listFilter.tool_types.length} ${t('toolManagement.filterByType')}`
            : null,
          listFilter.order === 'desc' ? t('toolManagement.sortDesc') : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : '';

  const importButton = (
    <ActionButton icon={<UploadSimple size={16} aria-hidden="true" />} onClick={() => setShowImportDialog(true)}>
      {t('toolManagement.importButton')}
    </ActionButton>
  );

  const addToolButton = (
    <ActionButton
      variant="primary"
      icon={<Plus size={16} aria-hidden="true" />}
      onClick={() => setShowAddToolDialog(true)}
    >
      {t('toolManagement.add')}
    </ActionButton>
  );

  return (
    <div className="flex flex-col gap-6">
      <Reveal>
        <PageHeader
          title={t('toolManagement.title')}
          subtitle={subtitle || undefined}
          backTo={{ to: '/', label: t('toolManagement.backToProjects') }}
          actions={
            dirtyCount > 0 || editingCell !== null ? (
              <ActionButton variant="primary" loading={submitting} onClick={handleSubmit}>
                {t('toolManagement.submit')}
                {dirtyCount > 0 ? (
                  <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold tabular-nums">
                    {dirtyCount}
                  </span>
                ) : null}
              </ActionButton>
            ) : null
          }
        />
      </Reveal>

      {error && <ErrorMessage message={error} />}

      <Reveal delayMs={80}>
        <section aria-labelledby="tools-heading" className="flex flex-col gap-4">
          <h2 id="tools-heading" className="sr-only">
            {t('toolManagement.title')}
          </h2>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <ActionButton
                icon={<FunnelSimple size={16} aria-hidden="true" />}
                onClick={() => setShowFilterModal(true)}
              >
                {t('toolManagement.openFilter')}
              </ActionButton>

              {/* The count is the advertisement: thirteen of the twenty-two columns are off by
                  default, and "9 / 22" on the button is the only thing on the page that says
                  there is more of the catalogue to see. */}
              <ActionButton
                icon={<Columns size={16} aria-hidden="true" />}
                onClick={(e) => columnChooserRef.current?.toggle(e)}
                aria-haspopup="dialog"
              >
                {t('toolManagement.columns')}
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-600">
                  {`${orderedColumns.length} / ${COLUMNS.length}`}
                </span>
              </ActionButton>
              <OverlayPanel
                ref={columnChooserRef}
                pt={{
                  root: { role: 'dialog', 'aria-label': t('toolManagement.columnsAriaLabel') },
                  content: { className: '!p-0' },
                }}
              >
                <ColumnChooser
                  visibleKeys={visibleColKeys}
                  onToggle={handleToggleColumn}
                  onApplyPreset={handleApplyColumnPreset}
                  onReset={handleResetColumns}
                />
              </OverlayPanel>

              {listFilterSummary ? <span className="text-sm text-gray-500">{listFilterSummary}</span> : null}

              {/* Exact-value filters live here rather than in a strip of their own above the
                  table, so every way of narrowing the list sits on one row. */}
              {activeFilterEntries.map(([field, value]) => (
                <span
                  key={field}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700"
                >
                  <span className="font-medium">{filterLabel(field, value)}</span>
                  <button
                    type="button"
                    onClick={() => clearFilter(field)}
                    className={`rounded-full p-0.5 hover:bg-slate-200 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
                    aria-label={t('common.remove')}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </span>
              ))}

              <ActionButton
                variant="ghost"
                iconOnly={true}
                icon={<Question size={16} aria-hidden="true" />}
                onClick={(e) => rowInteractionHelpRef.current?.toggle(e)}
                aria-label={t('toolManagement.rowInteractionHelpAriaLabel')}
                aria-haspopup="dialog"
              />
              <OverlayPanel
                ref={rowInteractionHelpRef}
                pt={{
                  root: { role: 'dialog', 'aria-label': t('toolManagement.rowInteractionHelpAriaLabel') },
                  content: { className: '!px-4 !py-3' },
                }}
              >
                <ul className="m-0 flex list-none flex-col gap-2 p-0 text-xs text-gray-500">
                  <li>{t('toolManagement.rowInteractionHintRow')}</li>
                  <li>{t('toolManagement.rowInteractionHintEdit')}</li>
                  <li>{t('toolManagement.rowInteractionHintFilter')}</li>
                  <li>{t('toolManagement.rowInteractionHintColumns')}</li>
                  <li>{t('toolManagement.rowInteractionHintColumnsChooser')}</li>
                </ul>
              </OverlayPanel>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {importButton}
              <ActionButton
                icon={<MagicWand size={16} aria-hidden="true" />}
                onClick={() => setShowPredictDialog(true)}
              >
                {t('toolManagement.predictToolsAriaLabel')}
              </ActionButton>
              {addToolButton}
            </div>
          </div>

          {isLoading ? (
            <TableSkeleton rows={10} columns={4} withThumbnail={true} />
          ) : tools.length === 0 ? (
            <div className={TABLE_CARD}>
              <EmptyState
                icon={<Toolbox size={28} aria-hidden="true" />}
                title={t('toolManagement.emptyTitle')}
                message={t('toolManagement.emptyMessage')}
                action={
                  <>
                    {importButton}
                    {addToolButton}
                  </>
                }
              />
            </div>
          ) : (
            <div className={`${TABLE_CARD} ${CARD_CONTAINMENT}`}>
              <div ref={attachTableScroll} onScroll={syncScrolledEdges} className="overflow-x-auto">
                <table className="w-full table-fixed text-sm" style={{ minWidth: totalTableWidth }}>
                  <colgroup>
                    {orderedColumns.map((col) => (
                      <col key={col.key} style={{ width: columnWidth(col.key) }} />
                    ))}
                    <col style={{ width: actionsColWidth }} />
                  </colgroup>
                  <thead>
                    <tr className={TABLE_HEADER_ROW}>
                      {orderedColumns.map((col) => {
                        const isPinned = col.key === PINNED_COLUMN;
                        // `sticky` is itself a positioned value, so a pinned cell must not
                        // also ask for `relative`: both are position utilities and which one
                        // wins would come down to stylesheet order, not to this class list.
                        const pinnedClasses = isPinned
                          ? scrolledEdges.start
                            ? STICKY_LEFT_HEADER_ELEVATED
                            : STICKY_LEFT_HEADER
                          : 'relative cursor-grab active:cursor-grabbing';
                        return (
                          <th
                            key={col.key}
                            scope="col"
                            draggable={!isPinned}
                            onDragStart={isPinned ? undefined : (e) => handleColDragStart(col.key, e)}
                            onDragOver={isPinned ? undefined : (e) => handleColDragOver(col.key, e)}
                            onDragLeave={isPinned ? undefined : handleColDragLeave}
                            onDrop={isPinned ? undefined : (e) => handleColDrop(col.key, e)}
                            onDragEnd={isPinned ? undefined : handleColDragEnd}
                            className={`group select-none overflow-hidden px-3 ${TABLE_HEADER_CELL} ${MOTION} ${
                              ALIGN_TEXT_CLASS[col.align]
                            } ${pinnedClasses} ${dragSourceKey === col.key ? 'opacity-40' : ''} ${
                              dragOverKey === col.key ? 'bg-gray-100' : ''
                            }`}
                            // Every header carries its own label, not only the two that need
                            // explaining: the defaults are sized so nothing truncates, but the
                            // user can drag any column narrower than its own heading.
                            title={
                              col.key === 'holders'
                                ? t('toolManagement.holdersFromAssemblies')
                                : col.key === 'ncNumbers'
                                  ? t('toolManagement.ncNumbersFromAssemblies')
                                  : t(col.labelKey)
                            }
                          >
                            {dragOverKey === col.key && (
                              <span className="absolute left-0 top-0 z-20 h-full w-0.5 bg-primary" aria-hidden="true" />
                            )}
                            <span className={`flex min-w-0 items-center gap-1 ${ALIGN_JUSTIFY_CLASS[col.align]}`}>
                              {!isPinned && (
                                <DotsSixVertical
                                  size={12}
                                  className={`shrink-0 text-gray-300 opacity-0 group-hover:opacity-100 ${MOTION}`}
                                  aria-hidden="true"
                                />
                              )}
                              <span className="truncate">{t(col.labelKey)}</span>
                              {col.unit ? <span className="shrink-0 text-gray-400">{`(${col.unit})`}</span> : null}
                            </span>
                            {/* Mouse-only affordance: there is no keyboard gesture to expose. */}
                            <div
                              className="absolute right-0 top-0 z-10 flex h-full w-2 cursor-col-resize items-center justify-center"
                              onMouseDown={(e) => handleResizeMouseDown(col.key, e)}
                              onDragStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              aria-hidden="true"
                            >
                              <span className={`block h-3.5 w-px rounded-full bg-gray-200 group-hover:bg-gray-300 ${MOTION}`} />
                            </div>
                          </th>
                        );
                      })}
                      <th
                        scope="col"
                        className={`px-2 ${
                          scrolledEdges.end ? STICKY_RIGHT_HEADER_ELEVATED : STICKY_RIGHT_HEADER
                        }`}
                      >
                        <span className="sr-only">{t('common.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTools.length === 0 ? (
                      <tr>
                        <td colSpan={orderedColumns.length + 1} className="px-4 py-10 text-center text-gray-500">
                          {t('toolManagement.noMatchFilter')}
                        </td>
                      </tr>
                    ) : (
                      filteredTools.map((tool) => {
                        const toolEdits = edits[tool.id];
                        const assemblies = assembliesByToolId.get(tool.id) ?? [];
                        return (
                          // `group` so the delete button can step up a shade while the row is
                          // hovered — the affordance has to read on every row, not only under
                          // the pointer.
                          <tr key={tool.id} className={`${TABLE_ROW} group`}>
                            {orderedColumns.map((col) => {
                              const key = col.key;
                              // A pinned cell is already positioned by `sticky`, which is
                              // what the pending-edit accent anchors to; adding `relative`
                              // as well would leave the winner up to stylesheet order.
                              const cellClass = `overflow-hidden px-3 align-middle ${
                                key === PINNED_COLUMN
                                  ? scrolledEdges.start
                                    ? STICKY_LEFT_CELL_ELEVATED
                                    : STICKY_LEFT_CELL
                                  : 'relative'
                              }`;

                              // Holder and NC number come from the tool's assemblies, so
                              // these two cells are read-only.
                              if (isDerivedColumn(key)) {
                                const missingTitle =
                                  assembliesError != null ? t('toolManagement.assembliesLoadError') : undefined;
                                if (key === 'ncNumbers') {
                                  const numbers = ncNumbersOf(assemblies);
                                  return (
                                    <td key={key} className={cellClass}>
                                      {numbers.length > 0 ? (
                                        <span
                                          className="flex items-center gap-1 overflow-hidden"
                                          title={numbers.join(', ')}
                                        >
                                          {numbers.map((ncNumber) => (
                                            <span
                                              key={ncNumber}
                                              className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-600"
                                            >
                                              {ncNumber}
                                            </span>
                                          ))}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400" title={missingTitle}>
                                          {assembliesLoading ? PENDING_VALUE : NO_VALUE}
                                        </span>
                                      )}
                                    </td>
                                  );
                                }
                                const names = holderNamesOf(assemblies);
                                const holderText =
                                  names.length > 0 ? names.join(', ') : assembliesLoading ? PENDING_VALUE : NO_VALUE;
                                return (
                                  <td key={key} className={cellClass}>
                                    <span
                                      className={`block truncate ${
                                        names.length > 0 ? 'text-gray-700' : 'text-gray-400'
                                      }`}
                                      title={names.length > 0 ? holderText : missingTitle}
                                    >
                                      {holderText}
                                    </span>
                                  </td>
                                );
                              }

                              // Server bookkeeping the update payload has no field for. The
                              // predicate is also what narrows `key` down to a writable field
                              // for every branch below it.
                              if (isReadOnlyColumn(key)) {
                                const raw = (tool as unknown as Record<string, unknown>)[key];
                                const text =
                                  col.kind === 'date' && typeof raw === 'string' ? formatDateTime(raw) : NO_VALUE;
                                return (
                                  <td key={key} className={cellClass}>
                                    <span
                                      className="block truncate px-2 py-1 tabular-nums text-gray-500"
                                      title={text}
                                    >
                                      {text}
                                    </span>
                                  </td>
                                );
                              }

                              const field = key;
                              const isEditing = editingCell?.toolId === tool.id && editingCell?.field === field;
                              const value = getCellValue<unknown>(tool, tool.id, field, edits);
                              const displayVal = value ?? '';
                              /** A measurement, or `null` for the nullable columns that have none. */
                              const numericValue =
                                typeof value === 'number' && Number.isFinite(value) ? value : null;
                              const isFilterActive =
                                filterValues[field] !== undefined &&
                                valuesEqual(filterValues[field]!, displayVal as string | number | boolean);
                              // A thin accent beats an amber wash: it marks the cell without
                              // repainting a whole row of them.
                              const pendingAccent =
                                toolEdits != null && field in toolEdits ? (
                                  <span className="absolute inset-y-0 left-0 w-[3px] bg-amber-400" aria-hidden="true" />
                                ) : null;
                              const valueClass = `block w-full truncate rounded px-2 py-1 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                                ALIGN_TEXT_CLASS[col.align]
                              } ${
                                isFilterActive
                                  ? 'bg-slate-100 font-medium text-slate-900'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`;

                              // The pinned first column: silhouette, then the name.
                              if (field === 'tool_name') {
                                const drawingAssembly = assemblies[0] ?? null;
                                const openDrawingLabel = t('toolManagement.openAssemblyDrawingFor', {
                                  toolName: tool.tool_name,
                                });
                                return (
                                  <td key={field} className={cellClass}>
                                    {pendingAccent}
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => openAssemblyDrawing(tool)}
                                        title={openDrawingLabel}
                                        aria-label={openDrawingLabel}
                                        className="flex shrink-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                      >
                                        <AssemblyThumbnail
                                          assemblyId={drawingAssembly?.id ?? null}
                                          ncNumber={drawingAssembly?.nc_number ?? null}
                                          extraCount={Math.max(0, assemblies.length - 1)}
                                          className="h-[42px] w-[30px]"
                                        />
                                      </button>
                                      {isEditing ? (
                                        <InputText
                                          value={String(displayVal)}
                                          onChange={(e) => setEdit(tool.id, field, e.target.value)}
                                          onBlur={() => setEditingCell(null)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                              e.preventDefault();
                                              setEditingCell(null);
                                            }
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              handleSubmit();
                                            }
                                          }}
                                          className="w-full p-inputtext-sm"
                                          autoFocus={true}
                                        />
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={(e) =>
                                            handleCellClick(
                                              field,
                                              displayVal as string | number | boolean,
                                              e.ctrlKey || e.metaKey
                                            )
                                          }
                                          onDoubleClick={() => handleCellDoubleClick(tool.id, field)}
                                          className={`${valueClass} min-w-0 flex-1`}
                                          title={String(displayVal)}
                                        >
                                          {String(displayVal)}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                );
                              }

                              // Classification columns: plain text until double-clicked, then a
                              // dropdown that opens on its own and closes when it is done.
                              if (col.kind === 'choice') {
                                const choice = (displayVal === '' ? null : String(displayVal)) as string | null;
                                const choiceText =
                                  choice == null
                                    ? NO_VALUE
                                    : field === 'purpose'
                                      ? t(`toolManagement.purpose_${choice}`)
                                      : choice;
                                const isToolTypeUpdating =
                                  field === 'tool_type' && toolTypeUpdatingToolId === tool.id;
                                return (
                                  <td key={field} className={cellClass}>
                                    {pendingAccent}
                                    {isEditing ? (
                                      <ChoiceCellEditor
                                        value={choice}
                                        options={field === 'tool_type' ? toolTypeOptions : purposeOptions}
                                        ariaLabel={t(col.labelKey)}
                                        placeholder={NO_VALUE}
                                        onChange={(picked) => {
                                          setEditingCell(null);
                                          if (field === 'tool_type') {
                                            handleToolTypeSelect(tool.id, picked as ToolType);
                                          } else {
                                            setEdit(tool.id, 'purpose', picked as string);
                                          }
                                        }}
                                        onClose={() => setEditingCell(null)}
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={(e) =>
                                          handleCellClick(field, (choice ?? '') as string, e.ctrlKey || e.metaKey)
                                        }
                                        onDoubleClick={() => handleCellDoubleClick(tool.id, field)}
                                        className={valueClass}
                                        title={choiceText}
                                      >
                                        {isToolTypeUpdating ? (
                                          <span className="inline-flex max-w-full items-center gap-2">
                                            <CircleNotch size={13} className="shrink-0 animate-spin" aria-hidden="true" />
                                            <span className="truncate">{choiceText}</span>
                                          </span>
                                        ) : (
                                          choiceText
                                        )}
                                      </button>
                                    )}
                                  </td>
                                );
                              }

                              // Yes/no columns. `ball_type` edits through the same dropdown as the
                              // classification cells; `active` does not edit at all — retiring a
                              // tool is a decision, not something a stray double-click should do —
                              // so it reads as a status chip instead.
                              if (col.kind === 'boolean') {
                                const flag = typeof displayVal === 'boolean' ? displayVal : null;
                                if (isEditing && col.editable) {
                                  return (
                                    <td key={field} className={cellClass}>
                                      {pendingAccent}
                                      <ChoiceCellEditor
                                        value={flag == null ? null : String(flag)}
                                        options={booleanOptions}
                                        ariaLabel={t(col.labelKey)}
                                        placeholder={NO_VALUE}
                                        onChange={(picked) => {
                                          setEditingCell(null);
                                          if (picked != null) setEdit(tool.id, field, picked === 'true');
                                        }}
                                        onClose={() => setEditingCell(null)}
                                      />
                                    </td>
                                  );
                                }
                                // `active` reads as a state ("Aktivní" / "Vyřazený") rather than
                                // as an answer ("Ano" / "Ne"), and a retired tool is the one case
                                // worth colouring: it is still in the catalogue but no longer in
                                // the shop.
                                const isStatusColumn = field === 'active';
                                const booleanText =
                                  flag == null
                                    ? NO_VALUE
                                    : isStatusColumn
                                      ? t(flag ? 'toolManagement.activeYes' : 'toolManagement.activeNo')
                                      : t(flag ? 'common.yes' : 'common.no');
                                const isRetired = isStatusColumn && flag === false;
                                const chipClass = `${STATUS_CHIP} ${
                                  isRetired ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-700'
                                }`;
                                return (
                                  <td key={field} className={cellClass}>
                                    {pendingAccent}
                                    <button
                                      type="button"
                                      onClick={(e) =>
                                        handleCellClick(field, displayVal as string | number | boolean, e.ctrlKey || e.metaKey)
                                      }
                                      onDoubleClick={
                                        col.editable ? () => handleCellDoubleClick(tool.id, field) : undefined
                                      }
                                      className={valueClass}
                                      title={booleanText}
                                    >
                                      <span className={chipClass}>{booleanText}</span>
                                    </button>
                                  </td>
                                );
                              }

                              if (isEditing && col.editable && col.kind === 'text') {
                                return (
                                  <td key={field} className={cellClass}>
                                    {pendingAccent}
                                    <InputText
                                      value={String(displayVal)}
                                      onChange={(e) => setEdit(tool.id, field, e.target.value)}
                                      onBlur={() => setEditingCell(null)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setEditingCell(null);
                                        }
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleSubmit();
                                        }
                                      }}
                                      className="w-full p-inputtext-sm"
                                      autoFocus={true}
                                    />
                                  </td>
                                );
                              }

                              if (isEditing && col.editable && col.kind === 'number') {
                                return (
                                  <td key={field} className={cellClass}>
                                    {pendingAccent}
                                    <InputNumber
                                      value={numericValue}
                                      onValueChange={(e) => {
                                        // A cleared input on a required column is a zero; on a
                                        // nullable one it is a null, which `setEdit` reads as
                                        // "drop the pending edit" rather than "store null".
                                        setEdit(tool.id, field, e.value ?? (col.nullable ? null : 0));
                                      }}
                                      onBlur={() => setEditingCell(null)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setEditingCell(null);
                                        }
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          // Blur triggers PrimeReact's internal commit
                                          // (onValueChange), which synchronously updates
                                          // editsRef before handleSubmit reads it.
                                          (e.target as HTMLElement).blur();
                                          handleSubmit();
                                        }
                                        blockInvalidNumberKeys(e);
                                      }}
                                      className="w-full p-inputnumber-sm"
                                      inputClassName={ALIGN_TEXT_CLASS[col.align]}
                                      mode="decimal"
                                      minFractionDigits={0}
                                      maxFractionDigits={4}
                                      autoFocus={true}
                                    />
                                  </td>
                                );
                              }

                              const readOnlyText =
                                col.kind === 'number'
                                  ? numericValue == null
                                    ? ''
                                    : formatMm(numericValue)
                                  : typeof displayVal === 'boolean'
                                    ? displayVal
                                      ? t('common.yes')
                                      : t('common.no')
                                    : String(displayVal);
                              return (
                                <td key={field} className={cellClass}>
                                  {pendingAccent}
                                  <button
                                    type="button"
                                    onClick={(e) =>
                                      handleCellClick(
                                        field,
                                        displayVal as string | number | boolean,
                                        e.ctrlKey || e.metaKey
                                      )
                                    }
                                    onDoubleClick={() => handleCellDoubleClick(tool.id, field)}
                                    className={`${valueClass} ${col.kind === 'number' ? 'tabular-nums' : ''}`}
                                    title={readOnlyText}
                                  >
                                    {readOnlyText || NO_VALUE}
                                  </button>
                                </td>
                              );
                            })}
                            <td
                              className={`px-2 text-right ${
                                scrolledEdges.end ? STICKY_RIGHT_CELL_ELEVATED : STICKY_RIGHT_CELL
                              }`}
                            >
                              {/* `hover:text-red-600!` re-states what `danger-ghost` already
                                  says, because `hover:` and `group-hover:` set the same
                                  property at the same specificity and which one wins is
                                  decided by the order Tailwind emitted them in. The `!` takes
                                  that out of the stylesheet's hands. */}
                              <ActionButton
                                variant="danger-ghost"
                                iconOnly={true}
                                icon={<Trash size={15} aria-hidden="true" />}
                                onClick={() => handleDeleteTool(tool.id)}
                                title={t('common.delete')}
                                aria-label={t('common.delete')}
                                className="group-hover:text-gray-700 hover:text-red-600!"
                              />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className={TABLE_FOOTER}>
                <div className="flex flex-1 items-center justify-center gap-2">
                  <ActionButton
                    variant="ghost"
                    iconOnly={true}
                    icon={<CaretLeft size={16} aria-hidden="true" />}
                    disabled={!hasPrevPage}
                    onClick={() => setPage(Math.max(1, page - 1))}
                    aria-label={t('common.previousPage')}
                  />
                  <span className="text-sm text-gray-600">
                    {totalPages != null
                      ? `${t('common.page')} ${page} ${t('common.of')} ${totalPages}`
                      : `${t('common.page')} ${page}`}
                  </span>
                  <ActionButton
                    variant="ghost"
                    iconOnly={true}
                    icon={<CaretRight size={16} aria-hidden="true" />}
                    disabled={!hasNextPage}
                    onClick={() => setPage(page + 1)}
                    aria-label={t('common.nextPage')}
                  />
                </div>
                {totalCount != null && (
                  <span className="shrink-0 text-sm text-gray-600">
                    {activeFilterEntries.length > 0
                      ? `${filteredTools.length} / ${tools.length}`
                      : `${Math.min(page * pageSize, totalCount)} / ${totalCount}`}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>
      </Reveal>

      <Reveal delayMs={160}>
        <section aria-labelledby="holders-heading" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 id="holders-heading" className="text-xl font-semibold text-gray-900">
                {t('holders.title')}
              </h2>
              <ActionButton
                variant="ghost"
                iconOnly={true}
                id="holder-dimensions-info-btn"
                icon={<Info size={18} aria-hidden="true" />}
                onClick={() => setShowDimensionsModal(true)}
                aria-label={t('holders.showDimensionsDiagram')}
              />
              <Tooltip target="#holder-dimensions-info-btn" position="bottom">
                {t('holders.showDimensionsDiagram')}
              </Tooltip>
            </div>
            <ActionButton
              icon={<Plus size={16} aria-hidden="true" />}
              onClick={() => setShowAddHolderDialog(true)}
            >
              {t('holders.add')}
            </ActionButton>
          </div>

          {holdersLoading ? (
            <TableSkeleton rows={6} columns={4} />
          ) : holders.length === 0 ? (
            <div className={TABLE_CARD}>
              <EmptyState
                icon={<Wrench size={28} aria-hidden="true" />}
                title={t('holders.noHolders')}
                message={t('holders.noHoldersMessage')}
                action={
                  <ActionButton
                    icon={<Plus size={16} aria-hidden="true" />}
                    onClick={() => setShowAddHolderDialog(true)}
                  >
                    {t('holders.add')}
                  </ActionButton>
                }
              />
            </div>
          ) : (
            <div className={`${TABLE_CARD} ${CARD_CONTAINMENT}`}>
              <div className="overflow-x-auto">
                {/* `table-fixed` with a colgroup, like the tools table above, so the name
                    column keeps its width instead of being pushed around by the longest
                    holder name. */}
                <table className="w-full table-fixed text-sm" style={{ minWidth: HOLDERS_TABLE_MIN_WIDTH }}>
                  <colgroup>
                    <col style={{ width: 240 }} />
                    {HOLDER_DIMENSION_FIELDS.map((field) => (
                      <col key={field} style={{ width: 112 }} />
                    ))}
                    <col style={{ width: 64 }} />
                  </colgroup>
                  <thead>
                    <tr className={TABLE_HEADER_ROW}>
                      <th scope="col" className={`${TABLE_HEADER_CELL} px-4 text-left`}>
                        {t('holders.holderName')}
                      </th>
                      <th scope="col" className={`${TABLE_HEADER_CELL} px-4 text-center`}>
                        {t('holders.diameterMm1')}
                      </th>
                      <th scope="col" className={`${TABLE_HEADER_CELL} px-4 text-center`}>
                        {t('holders.diameterMm2')}
                      </th>
                      <th scope="col" className={`${TABLE_HEADER_CELL} px-4 text-center`}>
                        {t('holders.lenMm1')}
                      </th>
                      <th scope="col" className={`${TABLE_HEADER_CELL} px-4 text-center`}>
                        {t('holders.lenMm2')}
                      </th>
                      <th scope="col" className={`${TABLE_HEADER_CELL} px-4 text-right`}>
                        <span className="sr-only">{t('common.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {holders.map((holder) => (
                      <tr
                        key={holder.id}
                        className={TABLE_ROW}
                      >
                        <td className="px-4 align-middle">
                          {editingHolderCell?.holderId === holder.id &&
                          editingHolderCell?.field === 'holder_name' ? (
                            <InputText
                              value={typeof holderEditValue === 'string' ? holderEditValue : ''}
                              onChange={(e) => setHolderEditValue(e.target.value)}
                              onBlur={() => {
                                if (holderEditValue !== null)
                                  handleHolderCellSave(holder.id, 'holder_name', holderEditValue);
                                else setEditingHolderCell(null);
                                setHolderEditValue(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setEditingHolderCell(null);
                                  setHolderEditValue(null);
                                }
                                if (e.key === 'Enter') {
                                  const v = (e.target as HTMLInputElement).value;
                                  handleHolderCellSave(holder.id, 'holder_name', v);
                                }
                              }}
                              className="w-full p-inputtext-sm"
                              autoFocus
                            />
                          ) : (
                            <button
                              type="button"
                              onDoubleClick={() => startEditingHolderCell(holder, 'holder_name')}
                              className={`block w-full whitespace-nowrap rounded px-2 py-1 text-left text-gray-700 hover:bg-gray-100 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
                              title={t('holders.doubleClickToEdit')}
                            >
                              {holder.holder_name || NO_VALUE}
                            </button>
                          )}
                        </td>
                        {HOLDER_DIMENSION_FIELDS.map((field) => (
                          <td key={field} className="px-4 align-middle">
                            {editingHolderCell?.holderId === holder.id && editingHolderCell?.field === field ? (
                              <InputNumber
                                value={typeof holderEditValue === 'number' ? holderEditValue : holder[field]}
                                onValueChange={(e) => setHolderEditValue(e.value ?? 0)}
                                onBlur={() => {
                                  if (holderEditValue !== null)
                                    handleHolderCellSave(holder.id, field, holderEditValue);
                                  setEditingHolderCell(null);
                                  setHolderEditValue(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') {
                                    setEditingHolderCell(null);
                                    setHolderEditValue(null);
                                  }
                                }}
                                className="w-full p-inputnumber-sm"
                                inputClassName="text-center"
                                mode="decimal"
                                autoFocus
                              />
                            ) : (
                              <button
                                type="button"
                                onDoubleClick={() => startEditingHolderCell(holder, field)}
                                className={`block w-full rounded px-2 py-1 text-center tabular-nums text-gray-700 hover:bg-gray-100 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
                                title={t('holders.doubleClickToEdit')}
                              >
                                {formatMm(holder[field])}
                              </button>
                            )}
                          </td>
                        ))}
                        <td className="px-4 text-right align-middle">
                          <ActionButton
                            variant="danger-ghost"
                            iconOnly={true}
                            icon={<Trash size={15} aria-hidden="true" />}
                            onClick={() => handleDeleteHolder(holder.id)}
                            title={t('common.delete')}
                            aria-label={t('common.delete')}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </Reveal>

      <Dialog
        header={t('toolManagement.importDialogTitle')}
        visible={showImportDialog}
        onHide={() => setShowImportDialog(false)}
        className="w-full max-w-lg"
        pt={TOOL_MANAGEMENT_DIALOG_PT}
      >
        <ToolImportDropZone onImported={handleToolsImported} />
      </Dialog>

      <Dialog
        header={t('toolManagement.filterDialogTitle')}
        visible={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        className="w-full max-w-md"
        pt={TOOL_MANAGEMENT_DIALOG_PT}
        footer={
          <div className="flex justify-end gap-2">
            <ActionButton
              id="filter-reset-btn"
              variant="ghost"
              onClick={() => {
                setFilterDraftToolTypes([...TOOL_TYPE_VALUES]);
                setFilterDraftOrder('asc');
              }}
            >
              {t('toolManagement.filterReset')}
            </ActionButton>
            <ActionButton variant="ghost" onClick={() => setShowFilterModal(false)}>
              {t('common.close')}
            </ActionButton>
            <ActionButton
              variant="primary"
              onClick={() => {
                const toolTypes =
                  filterDraftToolTypes.length === TOOL_TYPE_VALUES.length
                    ? null
                    : filterDraftToolTypes.length === 0
                      ? []
                      : filterDraftToolTypes;
                setListFilter({
                  tool_types: toolTypes,
                  order: filterDraftOrder,
                });
                setShowFilterModal(false);
              }}
            >
              {t('toolManagement.filterApply')}
            </ActionButton>
          </div>
        }
      >
        <Tooltip target="#filter-reset-btn">{t('toolManagement.filterResetTooltip')}</Tooltip>
        <div className="flex flex-col gap-6 pt-1">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                {t('toolManagement.filterByType')}
              </span>
              <button
                type="button"
                onClick={() =>
                  filterDraftToolTypes.length === TOOL_TYPE_VALUES.length
                    ? setFilterDraftToolTypes([])
                    : setFilterDraftToolTypes([...TOOL_TYPE_VALUES])
                }
                className={`rounded text-xs font-semibold text-gray-600 hover:text-gray-900 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
              >
                {filterDraftToolTypes.length === TOOL_TYPE_VALUES.length
                  ? t('toolManagement.filterDeselectAll')
                  : t('toolManagement.filterSelectAll')}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-0.5">
              {TOOL_TYPE_VALUES.map((tt) => (
                <div
                  key={tt}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 ${MOTION}`}
                >
                  <Checkbox
                    inputId={`filter-tt-${tt}`}
                    checked={filterDraftToolTypes.includes(tt)}
                    onChange={(e) =>
                      setFilterDraftToolTypes((prev) => (e.checked ? [...prev, tt] : prev.filter((v) => v !== tt)))
                    }
                    unstyled
                    pt={{
                      root: { className: 'relative inline-flex cursor-pointer select-none shrink-0 w-4 h-4' },
                      input: {
                        className:
                          'absolute appearance-none top-0 left-0 size-full p-0 m-0 opacity-0 z-10 outline-none cursor-pointer',
                      },
                      box: {
                        className:
                          'w-4 h-4 border-2 border-gray-400 rounded bg-transparent flex items-center justify-center transition-colors data-[p-highlight=true]:bg-primary data-[p-highlight=true]:border-primary',
                      },
                      icon: { className: 'text-white w-2.5 h-2.5' },
                    }}
                  />
                  <label
                    htmlFor={`filter-tt-${tt}`}
                    className="cursor-pointer select-none text-sm leading-none text-gray-700"
                  >
                    {tt}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100" />

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('toolManagement.sortOrder')}
            </span>
            <div className="flex overflow-hidden rounded-lg border border-gray-200">
              {(['asc', 'desc'] as const).map((order, i) => (
                <button
                  key={order}
                  type="button"
                  onClick={() => setFilterDraftOrder(order)}
                  className={[
                    'flex-1 px-4 py-2 text-sm font-semibold',
                    MOTION,
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset',
                    i > 0 ? 'border-l border-gray-200' : '',
                    filterDraftOrder === order
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50',
                  ].join(' ')}
                >
                  {order === 'asc' ? t('toolManagement.sortAsc') : t('toolManagement.sortDesc')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={t('holders.dimensionsDiagramAlt')}
        visible={showDimensionsModal}
        onHide={() => setShowDimensionsModal(false)}
        className="w-auto max-w-lg"
        pt={TOOL_MANAGEMENT_DIALOG_PT}
      >
        <img
          src="/holder-dimensions.png"
          alt={t('holders.dimensionsDiagramAlt')}
          className="h-auto w-full object-contain"
        />
      </Dialog>

      <Dialog
        header={t('holders.add')}
        visible={showAddHolderDialog}
        onHide={() => setShowAddHolderDialog(false)}
        className="w-full max-w-md"
        pt={TOOL_MANAGEMENT_DIALOG_PT}
        footer={
          <div className="flex justify-end gap-2">
            <ActionButton variant="ghost" onClick={() => setShowAddHolderDialog(false)}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton variant="primary" loading={addingHolder} onClick={handleAddHolder}>
              {t('common.save')}
            </ActionButton>
          </div>
        }
      >
        <div className="flex flex-col gap-3 pt-2">
          <label className="font-medium text-gray-700" htmlFor="add-holder-name">
            {t('holders.holderName')}
          </label>
          <InputText
            id="add-holder-name"
            value={addHolderForm.holder_name ?? ''}
            onChange={(e) => setAddHolderForm((prev) => ({ ...prev, holder_name: e.target.value }))}
            className="w-full"
          />
          <label className="font-medium text-gray-700" htmlFor="add-holder-diameter-1">
            {t('holders.diameterMm1')}
          </label>
          <InputNumber
            inputId="add-holder-diameter-1"
            value={addHolderForm.diameter_mm_1}
            onValueChange={(e) => setAddHolderForm((prev) => ({ ...prev, diameter_mm_1: e.value ?? 0 }))}
            className="w-full"
            mode="decimal"
          />
          <label className="font-medium text-gray-700" htmlFor="add-holder-diameter-2">
            {t('holders.diameterMm2')}
          </label>
          <InputNumber
            inputId="add-holder-diameter-2"
            value={addHolderForm.diameter_mm_2}
            onValueChange={(e) => setAddHolderForm((prev) => ({ ...prev, diameter_mm_2: e.value ?? 0 }))}
            className="w-full"
            mode="decimal"
          />
          <label className="font-medium text-gray-700" htmlFor="add-holder-len-1">
            {t('holders.lenMm1')}
          </label>
          <InputNumber
            inputId="add-holder-len-1"
            value={addHolderForm.len_mm_1}
            onValueChange={(e) => setAddHolderForm((prev) => ({ ...prev, len_mm_1: e.value ?? 0 }))}
            className="w-full"
            mode="decimal"
          />
          <label className="font-medium text-gray-700" htmlFor="add-holder-len-2">
            {t('holders.lenMm2')}
          </label>
          <InputNumber
            inputId="add-holder-len-2"
            value={addHolderForm.len_mm_2}
            onValueChange={(e) => setAddHolderForm((prev) => ({ ...prev, len_mm_2: e.value ?? 0 }))}
            className="w-full"
            mode="decimal"
          />
        </div>
      </Dialog>

      <Dialog
        header={t('toolManagement.addDialogTitle')}
        visible={showAddToolDialog}
        onHide={() => setShowAddToolDialog(false)}
        className="w-full max-w-2xl"
        pt={TOOL_MANAGEMENT_DIALOG_PT}
        footer={
          <div className="flex justify-end gap-2 pt-2">
            <ActionButton variant="ghost" onClick={() => setShowAddToolDialog(false)}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton variant="primary" loading={addingTool} onClick={handleAddTool}>
              {t('common.save')}
            </ActionButton>
          </div>
        }
      >
        <div className="flex flex-col gap-6 pt-2">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('toolManagement.sectionBasic')}
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-name">
                  {t('toolManagement.toolName')} *
                </label>
                <InputText
                  id="add-tool-name"
                  value={addToolForm.tool_name}
                  onChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_name: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-type">
                  {t('toolManagement.toolType')} *
                </label>
                <Dropdown
                  inputId="add-tool-type"
                  value={addToolForm.tool_type}
                  options={TOOL_TYPE_VALUES.map((v) => ({ label: v, value: v }))}
                  onChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_type: e.value as ToolType }))}
                  className="w-full"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-comment">
                  {t('toolManagement.toolComment')}
                </label>
                <InputText
                  id="add-tool-comment"
                  value={addToolForm.tool_comment}
                  onChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_comment: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div className="col-span-2 flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-ball-type">
                  {t('toolManagement.ballType')}
                </label>
                <Dropdown
                  inputId="add-tool-ball-type"
                  value={ballTypeToDropdown(addToolForm.ball_type)}
                  options={
                    [
                      { label: t('toolManagement.ballTypeUnset'), value: 'unset' },
                      { label: t('common.yes'), value: 'true' },
                      { label: t('common.no'), value: 'false' },
                    ] as { label: string; value: BallTypeDropdownValue }[]
                  }
                  onChange={(e) => {
                    const v = e.value as BallTypeDropdownValue;
                    setAddToolForm((prev) => ({
                      ...prev,
                      // undefined, not null: the backend field is a non-null bool with a default
                      ball_type: v === 'true' ? true : v === 'false' ? false : undefined,
                    }));
                  }}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100" />

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('toolManagement.sectionDimensions')}
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-tool_diameter">
                  {t('toolManagement.toolDiameter')} *
                </label>
                <InputNumber
                  inputId="add-tool-tool_diameter"
                  value={addToolForm.tool_diameter}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_diameter: e.value ?? 0 }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-tool_overall_len">
                  {t('toolManagement.toolOverallLen')} *
                </label>
                <InputNumber
                  inputId="add-tool-tool_overall_len"
                  value={addToolForm.tool_overall_len}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_overall_len: e.value ?? 0 }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-tool_len">
                  {t('toolManagement.toolLen')} *
                </label>
                <InputNumber
                  inputId="add-tool-tool_len"
                  value={addToolForm.tool_len}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_len: e.value ?? 0 }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-tool_corner_rad">
                  {t('toolManagement.toolCornerRad')}
                </label>
                <InputNumber
                  inputId="add-tool-tool_corner_rad"
                  value={addToolForm.tool_corner_rad}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_corner_rad: e.value ?? null }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                  placeholder={NO_VALUE}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-tool_cut_angle">
                  {t('toolManagement.toolCutAngle')}
                </label>
                <InputNumber
                  inputId="add-tool-tool_cut_angle"
                  value={addToolForm.tool_cut_angle}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_cut_angle: e.value ?? null }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                  placeholder={NO_VALUE}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-tool_cut_len">
                  {t('toolManagement.toolCutLen')}
                </label>
                <InputNumber
                  inputId="add-tool-tool_cut_len"
                  value={addToolForm.tool_cut_len}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_cut_len: e.value ?? null }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                  placeholder={NO_VALUE}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-rotation">
                  {t('toolManagement.addToolRotation')}
                </label>
                <InputText
                  id="add-tool-rotation"
                  value={addToolForm.tool_rotation}
                  onChange={(e) => setAddToolForm((prev) => ({ ...prev, tool_rotation: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100" />

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('toolManagement.sectionAngles')}
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-horizontal_distance">
                  {t('toolManagement.horizontalDistance')}
                </label>
                <InputNumber
                  inputId="add-tool-horizontal_distance"
                  value={addToolForm.horizontal_distance}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, horizontal_distance: e.value ?? null }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                  placeholder={NO_VALUE}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-vertical_distance">
                  {t('toolManagement.verticalDistance')}
                </label>
                <InputNumber
                  inputId="add-tool-vertical_distance"
                  value={addToolForm.vertical_distance}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, vertical_distance: e.value ?? null }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                  placeholder={NO_VALUE}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-tip_angle">
                  {t('toolManagement.tipAngle')}
                </label>
                <InputNumber
                  inputId="add-tool-tip_angle"
                  value={addToolForm.tip_angle}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, tip_angle: e.value ?? null }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                  placeholder={NO_VALUE}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="add-tool-flank_angle">
                  {t('toolManagement.flankAngle')}
                </label>
                <InputNumber
                  inputId="add-tool-flank_angle"
                  value={addToolForm.flank_angle}
                  onValueChange={(e) => setAddToolForm((prev) => ({ ...prev, flank_angle: e.value ?? null }))}
                  className="w-full"
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                  showButtons={false}
                  placeholder={NO_VALUE}
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <PredictToolsDialog visible={showPredictDialog} onHide={() => setShowPredictDialog(false)} />

      <ToolImportReportDialog
        report={importReport}
        visible={showImportReport}
        onHide={() => setShowImportReport(false)}
      />

      <AssemblyDrawingDialog
        visible={drawingTool !== null}
        toolId={drawingTool?.id ?? null}
        toolName={drawingTool?.tool_name ?? ''}
        onClose={closeAssemblyDrawing}
      />
    </div>
  );
};
